import type { Edge, Node } from "@xyflow/react";
import { MarkerType } from "@xyflow/react";
import { type CSSProperties, useMemo } from "react";
import type { ArchVizState } from "@/components/archviz/ArchVizContext";
import type { ActorNodeData } from "@/components/archviz/nodes/ActorNode";
import type { ComponentNodeData } from "@/components/archviz/nodes/ComponentNode";
import type { ContainerNodeData } from "@/components/archviz/nodes/ContainerNode";
import type { EndpointNodeData } from "@/components/archviz/nodes/EndpointNode";
import type { EntityNodeData } from "@/components/archviz/nodes/EntityNode";
import type { ExternalSystemNodeData } from "@/components/archviz/nodes/ExternalSystemNode";
import type { PermissionNodeData } from "@/components/archviz/nodes/PermissionNode";
import type { SystemBoundaryNodeData } from "@/components/archviz/nodes/SystemBoundaryNode";
import type { SystemNodeData } from "@/components/archviz/nodes/SystemNode";
import {
  buildComponentToContainerMap,
  type CanonicalEdge,
  canonicalEdges,
  findStepLabel,
} from "@/lib/arch-canonical-edges";
import { layoutGraph } from "@/lib/arch-layout/dagre-layout";
import { resolveId } from "@/lib/arch-layout/id-resolver";
import {
  applyContainerPositions,
  applyExternalPositions,
  layoutContainersByRank,
  layoutExternalsAroundPerimeter,
} from "@/lib/arch-layout/kind-rank-layout";
import { radialLayout } from "@/lib/arch-layout/radial-layout";
import type { OverlayMode } from "@/lib/arch-overlay";
import {
  type ArchitectureData,
  type DataModel,
  type Entity,
  type EntityRelationship,
  type ExternalSystem,
  schemaSupportsApiSurface,
} from "@/lib/arch-parser";
import { groupEndpointsByPermission } from "@/lib/archviz/group-api-surface";

function nodeSize(node: Node): { w: number; h: number } {
  const w = typeof node.width === "number" ? node.width : ((node.style?.width as number) ?? 200);
  const h = typeof node.height === "number" ? node.height : ((node.style?.height as number) ?? 80);
  return { w, h };
}

function nodeCenter(node: Node): { x: number; y: number } {
  const { w, h } = nodeSize(node);
  return { x: node.position.x + w / 2, y: node.position.y + h / 2 };
}

type Side = "top" | "bottom" | "left" | "right";

// Midpoint of each side of a node's bounding rect. Used by routeEdges to
// pick the handle whose anchor is geometrically closest to the opposite
// node's center — a per-side picker, independent for source vs target,
// which allows mixed-axis pairs (e.g. source=top, target=left) when the
// nodes sit diagonally. The earlier max(|dy|,|dx|) heuristic forced
// source and target onto the same axis and produced long detours at L1
// when externals sit on the corner of the system node (gap #6/9).
function sideMidpoints(node: Node): Record<Side, { x: number; y: number }> {
  const { w, h } = nodeSize(node);
  const x = node.position.x;
  const y = node.position.y;
  return {
    top: { x: x + w / 2, y },
    bottom: { x: x + w / 2, y: y + h },
    left: { x, y: y + h / 2 },
    right: { x: x + w, y: y + h / 2 },
  };
}

function closestSide(node: Node, target: { x: number; y: number }): Side {
  const mids = sideMidpoints(node);
  let best: Side = "top";
  let bestDist = Infinity;
  for (const side of ["top", "bottom", "left", "right"] as const) {
    const m = mids[side];
    const dx = m.x - target.x;
    const dy = m.y - target.y;
    const d = dx * dx + dy * dy;
    if (d < bestDist) {
      bestDist = d;
      best = side;
    }
  }
  return best;
}

type RouteLevel = 1 | 2 | 3;

function pickHandlesL1(src: Node, tgt: Node): { sourceHandle: string; targetHandle: string } {
  // Closest-side picker: source and target chosen independently. Allows
  // mixed-axis pairs (top-src + left) when the radial layout puts an
  // external diagonally from the central system. Fixes long detours at
  // L1 corners (gap #6/9).
  const sourceSide = closestSide(src, nodeCenter(tgt));
  const targetSide = closestSide(tgt, nodeCenter(src));
  return { sourceHandle: `${sourceSide}-src`, targetHandle: targetSide };
}

function pickHandlesAxisDominant(
  src: Node,
  tgt: Node,
): { sourceHandle: string; targetHandle: string } {
  // Same-axis picker: source and target forced onto the dominant axis of
  // the dx/dy vector between their centers. Matches dagre's row-aligned
  // layout at L2/L3 — closest-side would otherwise pick mixed-axis pairs
  // that route edges through intermediate nodes when adjacent containers
  // sit slightly off-axis (gap #6/9.1).
  const sc = nodeCenter(src);
  const tc = nodeCenter(tgt);
  const dx = tc.x - sc.x;
  const dy = tc.y - sc.y;
  if (Math.abs(dy) > Math.abs(dx)) {
    return dy > 0
      ? { sourceHandle: "bottom-src", targetHandle: "top" }
      : { sourceHandle: "top-src", targetHandle: "bottom" };
  }
  return dx > 0
    ? { sourceHandle: "right-src", targetHandle: "left" }
    : { sourceHandle: "left-src", targetHandle: "right" };
}

function routeEdges(nodes: Node[], edges: Edge[], level: RouteLevel): Edge[] {
  const byId = new Map(nodes.map((n) => [n.id, n]));
  return edges.map((edge) => {
    const src = byId.get(edge.source);
    const tgt = byId.get(edge.target);
    if (!src || !tgt) return edge;
    const handles = level === 1 ? pickHandlesL1(src, tgt) : pickHandlesAxisDominant(src, tgt);
    return { ...edge, ...handles };
  });
}

function computeBBox(
  nodes: ReadonlyArray<Node>,
  fallback: { w: number; h: number },
): { minX: number; minY: number; maxX: number; maxY: number } | null {
  if (nodes.length === 0) return null;
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const n of nodes) {
    const w = typeof n.width === "number" ? n.width : fallback.w;
    const h = typeof n.height === "number" ? n.height : fallback.h;
    minX = Math.min(minX, n.position.x);
    minY = Math.min(minY, n.position.y);
    maxX = Math.max(maxX, n.position.x + w);
    maxY = Math.max(maxY, n.position.y + h);
  }
  return { minX, minY, maxX, maxY };
}

function upper(s: string | undefined): string | undefined {
  return s ? s.toUpperCase() : undefined;
}

// Compose the system-frame label + subtitle from meta fields.
// Returns the uppercase label and an optional uppercase subtitle
// composed as: "<RUNTIME> (<ENV1 + ENV2 + …>)". Each segment is
// independently optional — graceful degradation per spec §2.
function composeSystemFrameLabel(meta: ArchitectureData["meta"]): {
  label: string;
  subtitle: string | undefined;
} {
  const envs = (meta.environments ?? [])
    .map((e) => upper(e.name))
    .filter((s): s is string => Boolean(s));
  const runtime = upper(meta.runtime_context);
  const envPart = envs.length > 0 ? `(${envs.join(" + ")})` : undefined;
  const subtitleParts = [runtime, envPart].filter((s): s is string => Boolean(s));
  return {
    label: meta.name.toUpperCase(),
    subtitle: subtitleParts.length > 0 ? subtitleParts.join(" ") : undefined,
  };
}

function composeClusterSubtitle(
  meta: ArchitectureData["meta"],
  clusterId: string,
): string | undefined {
  return upper(meta.clusters?.[clusterId]?.runtime_context);
}

function addBoundaryFrame(
  nodes: Node[],
  label: string,
  subtitle: string | undefined,
  includeType: "container" | "component" | "entity",
  frameId: string,
): Node[] {
  const targets = nodes.filter((n) => n.type === includeType);
  const fallbackW = includeType === "container" ? 280 : includeType === "entity" ? 240 : 180;
  const fallbackH = includeType === "container" ? 140 : includeType === "entity" ? 80 : 60;
  const bbox = computeBBox(targets, { w: fallbackW, h: fallbackH });
  if (!bbox) return nodes;
  const PAD = 60;
  const TOP_PAD = 70;
  const frame: Node = {
    id: frameId,
    type: "systemFrame",
    position: { x: bbox.minX - PAD, y: bbox.minY - TOP_PAD },
    data: { label, subtitle },
    width: bbox.maxX - bbox.minX + PAD * 2,
    height: bbox.maxY - bbox.minY + TOP_PAD + PAD,
    style: {
      width: bbox.maxX - bbox.minX + PAD * 2,
      height: bbox.maxY - bbox.minY + TOP_PAD + PAD,
    },
    draggable: false,
    selectable: false,
    // Z-stack convention: systemFrame(-2) < edges(0) < clusterFrame(5) < content(10).
    // systemFrame is the outermost wrapper at L2/L3 — keep it the lowest layer
    // so edges, cluster sub-boundaries, and the content nodes themselves all
    // render in front of its border.
    zIndex: -2,
  };
  return [frame, ...nodes];
}

// L2 sub-boundary chrome. Padding leaves a comfortable inset between the
// dashed rect and the contained nodes; the top-padding is larger to give
// the uppercase label chip a clear zone above the contents (label sits
// inside the cluster, between border and contained nodes).
const CLUSTER_PAD = 20;
const CLUSTER_TOP_PAD = 38;

// Default arrowhead config for every edge (gap #6/5). Explicit size keeps
// the tip readable at standard zoom; `color` pins the marker to the same
// CSS var as the path stroke so theme flips stay in sync. globals.css
// sets a fallback on `.react-flow__arrowhead` for browsers that don't
// resolve CSS vars on the marker fill attribute.
const EDGE_MARKER = {
  type: MarkerType.ArrowClosed,
  width: 12,
  height: 12,
  color: "var(--edge-stroke)",
} as const;

function makeClusterFrame(
  id: string,
  label: string,
  subtitle: string | undefined,
  kind: "worker" | "external" | "system",
  borderStyle: "solid" | "dashed",
  contained: ReadonlyArray<Node>,
  fallback: { w: number; h: number },
): Node | null {
  const bbox = computeBBox(contained, fallback);
  if (!bbox) return null;
  const width = bbox.maxX - bbox.minX + CLUSTER_PAD * 2;
  const height = bbox.maxY - bbox.minY + CLUSTER_TOP_PAD + CLUSTER_PAD;
  return {
    id,
    type: "clusterFrame",
    position: { x: bbox.minX - CLUSTER_PAD, y: bbox.minY - CLUSTER_TOP_PAD },
    data: { label, subtitle, kind, borderStyle },
    width,
    height,
    style: { width, height },
    draggable: false,
    selectable: false,
    // Z-stack: edges(0) render below clusterFrame(5) so the cluster border
    // draws atop any edge that crosses it; content nodes(10) sit on top so
    // the externals/containers inside still occlude the cluster's border
    // where they overlap (gap #6/9).
    zIndex: 5,
  };
}

// Content nodes render above edges and above cluster sub-boundaries. Set
// explicitly on every actor/external/container/component/system node so
// the z-stack is uniform across L1/L2/L3 (gap #6/9).
const CONTENT_Z_INDEX = 10;

// Reorder externals so cluster members are contiguous before radialLayout
// places them. Clusters appear in first-appearance order; non-clustered
// externals keep their input order and follow all cluster groups. With
// radialLayout's default perRow=4, a 4-member cluster occupies one row
// cleanly and its bbox is a tidy rectangle.
function sortExternalsByCluster(
  externals: ReadonlyArray<ExternalSystem>,
): ReadonlyArray<ExternalSystem> {
  const clusterOrder: string[] = [];
  const buckets = new Map<string, ExternalSystem[]>();
  const free: ExternalSystem[] = [];
  for (const ext of externals) {
    const cid = ext.cluster?.id;
    if (!cid) {
      free.push(ext);
      continue;
    }
    let bucket = buckets.get(cid);
    if (!bucket) {
      bucket = [];
      buckets.set(cid, bucket);
      clusterOrder.push(cid);
    }
    bucket.push(ext);
  }
  const result: ExternalSystem[] = [];
  for (const cid of clusterOrder) {
    const bucket = buckets.get(cid);
    if (bucket) result.push(...bucket);
  }
  result.push(...free);
  return result;
}

// Group externals by their cluster.id, preserving first-appearance order.
// Externals without a cluster are skipped.
function groupExternalsByCluster(
  externals: ReadonlyArray<ExternalSystem>,
): Map<string, ExternalSystem[]> {
  const grouped = new Map<string, ExternalSystem[]>();
  for (const ext of externals) {
    const cid = ext.cluster?.id;
    if (!cid) continue;
    let bucket = grouped.get(cid);
    if (!bucket) {
      bucket = [];
      grouped.set(cid, bucket);
    }
    bucket.push(ext);
  }
  return grouped;
}

const ACTOR_NODE_SIZE = { width: 160, height: 60 };
// Container/external sizes match the worst-case rendered footprint of
// their inner components. Width matches `max-w-[240px]` in
// `ExternalSystemNode.tsx`; height is enforced by `h-[140px]` / `h-[200px]`
// on the card root (gap #6/9.3), so declared and rendered now agree
// instead of the card growing past the declared budget on wrapped labels.
//   - Compact (L1): icon + label + "External system · kind" footer only —
//     description block hidden. ~57 px label budget at h-[140px] is enough
//     for the worst wolf-dms labels (microsoft-graph 41 ch, google-gemini
//     42 ch) to wrap to 2 lines without clipping.
//   - Expanded (L2/L3): icon + label + headline + description + footer.
//     Worst case in wolf-dms is microsoft-purview (179-char description
//     wrapping to 4 lines) and microsoft-graph (label
//     "Microsoft Graph API (SharePoint + drives)" wrapping to 2 lines),
//     which together push rendered height near 200 px (gap #6/9.2).
// Width bumped from 220 to 240 in gap #6/9.3 to ease label wrap.
const EXTERNAL_NODE_SIZE_COMPACT = { width: 240, height: 140 };
const EXTERNAL_NODE_SIZE_EXPANDED = { width: 240, height: 200 };

// v0.4 drillability hint state. Surfaced through ExternalSystemNode as a
// cursor change + native `title=` text so users can tell apart three states
// that all look identical without this signal: (a) drillable, (b) Guardrail 2
// — intentionally non-drillable trivial external on a modern-schema doc,
// (c) non-drillable because the loaded doc predates v0.4 entirely and
// couldn't have carried permissions/endpoints regardless. The legacy state
// is the one that motivated this helper — pre-v0.4 uploads sitting in the
// DB after the v0.4 parser ships made `microsoft-graph` look broken on
// double-click. The hint tells the user to re-upload, not to file a bug.
export type ExternalDrillState = "drillable" | "non-drillable-modern" | "non-drillable-legacy";

function computeExternalDrillHint(
  ext: ExternalSystem,
  arch: ArchitectureData,
): { state: ExternalDrillState; title: string } {
  const hasSurface = (ext.permissions?.length ?? 0) > 0 || (ext.endpoints?.length ?? 0) > 0;
  if (hasSurface) {
    return { state: "drillable", title: "Double-click to inspect API surface" };
  }
  if (schemaSupportsApiSurface(arch.meta)) {
    return {
      state: "non-drillable-modern",
      title: "No API surface documented for this external",
    };
  }
  const schemaLabel = arch.meta.schema?.trim() ? arch.meta.schema.trim() : "(legacy)";
  return {
    state: "non-drillable-legacy",
    title: `API surface not available — this architecture was loaded under schema ${schemaLabel}. Re-upload from an archviz/v0.4 document to enable inspection.`,
  };
}
// ContainerNode: max-w-[280px], height up to ~125px with description +
// tech-chip row (border + padding + icon/label/kind/description stack +
// tech row). Rounded up to 140 for headroom.
const CONTAINER_NODE_SIZE = { width: 280, height: 140 };
const COMPONENT_NODE_SIZE = { width: 180, height: 60 };
// Width comfortably fits the system label + lifecycle chip; height shrank
// from 260 to 140 in gap #4 when description/why/drill hint moved to the
// side panel. The radial layout reads this to compute the orbit radius for
// actors/externals, so any further size change here ripples to L1 spacing.
const SYSTEM_NODE_SIZE = { width: 440, height: 140 };

function buildContainerExternalAdjacency(
  edges: ReadonlyArray<CanonicalEdge>,
  containerIds: ReadonlySet<string>,
  externalIds: ReadonlySet<string>,
): Map<string, Set<string>> {
  const map = new Map<string, Set<string>>();
  for (const e of edges) {
    if (containerIds.has(e.from) && externalIds.has(e.to)) {
      let set = map.get(e.from);
      if (!set) {
        set = new Set<string>();
        map.set(e.from, set);
      }
      set.add(e.to);
    }
    // Edges in the other direction (external → container) count too — a
    // container that receives data from an external is still on the
    // connectivity surface.
    if (externalIds.has(e.from) && containerIds.has(e.to)) {
      let set = map.get(e.to);
      if (!set) {
        set = new Set<string>();
        map.set(e.to, set);
      }
      set.add(e.from);
    }
  }
  return map;
}

function buildComponentExternalAdjacency(
  arch: ArchitectureData,
  externalIds: ReadonlySet<string>,
): Map<string, Set<string>> {
  // v0.2 docs only — v0.3 components have no connects_to. Walking
  // component.connects_to[] is safe for both paths (undefined on v0.3).
  const map = new Map<string, Set<string>>();
  for (const c of arch.containers) {
    for (const comp of c.components) {
      const targets = comp.connects_to ?? [];
      for (const t of targets) {
        if (!externalIds.has(t)) continue;
        let set = map.get(comp.id);
        if (!set) {
          set = new Set<string>();
          map.set(comp.id, set);
        }
        set.add(t);
      }
    }
  }
  return map;
}

type LabelMode = "always" | "hover-only" | "off";

interface BuildContext {
  labelMode: LabelMode;
  componentToContainer: ReadonlyMap<string, string>;
  externalIds: ReadonlySet<string>;
  containerIds: ReadonlySet<string>;
  actorIds: ReadonlySet<string>;
  edges: ReadonlyArray<CanonicalEdge>;
  // Per-container set of external IDs they reach (used by overlay matchers).
  containerExternals: ReadonlyMap<string, ReadonlySet<string>>;
  componentExternals: ReadonlyMap<string, ReadonlySet<string>>;
}

function decorateEdge(
  ctx: BuildContext,
  edge: CanonicalEdge,
): {
  label?: string;
  labelStyle?: CSSProperties;
  labelBgStyle?: CSSProperties;
  data?: { hoverLabel: string };
} {
  if (ctx.labelMode === "off" || !edge.label) return {};
  if (ctx.labelMode === "hover-only") {
    return { data: { hoverLabel: edge.label } };
  }
  return {
    label: edge.label,
    labelStyle: { fontSize: 10, fill: "var(--muted-foreground)" },
    labelBgStyle: { fill: "var(--card)", fillOpacity: 0.9 },
  };
}

function aggregateRiskCounts(arch: ArchitectureData): {
  total: number;
  containersWithRisk: number;
} {
  let total = 0;
  let containersWithRisk = 0;
  for (const s of arch.externalSystems) {
    total += s.risks?.length ?? s.risk.length;
  }
  for (const c of arch.containers) {
    const cRisk = c.risks?.length ?? c.risk.length;
    const compRisk = c.components.reduce((sum, x) => sum + (x.risks?.length ?? x.risk.length), 0);
    total += cRisk + compRisk;
    if (cRisk > 0 || compRisk > 0) containersWithRisk += 1;
  }
  return { total, containersWithRisk };
}

export function buildL1(
  arch: ArchitectureData,
  overlay: OverlayMode,
  ctx: BuildContext,
  layoutMode: "sides" | "compass" = "sides",
) {
  const nodes: Node[] = [];
  const edges: Edge[] = [];

  // L1 surfaces:
  //   - actors that appear as an endpoint of any canonical edge (clean rule —
  //     a person who interacts with nothing is noise),
  //   - EVERY authored external system, whether or not it's connected by a
  //     flow. Externals are the documented integration surface; a stranded
  //     external usually signals "we depend on this for auth / data / AI /
  //     compliance but haven't authored the flow yet" — hiding it makes the
  //     L1 diagram lie. The system→external edge below is still gated on a
  //     real flow, so orphan externals render as a node in the ring with no
  //     line to the central system, which visually communicates the gap.
  const referencedActorIds = new Set<string>();
  for (const e of ctx.edges) {
    if (ctx.actorIds.has(e.from)) referencedActorIds.add(e.from);
    if (ctx.actorIds.has(e.to)) referencedActorIds.add(e.to);
  }

  const visibleActors = arch.actors.filter((a) => referencedActorIds.has(a.id));
  const visibleExternals = arch.externalSystems;

  // Reorder so cluster members sit adjacent in the radial grid; bbox emit
  // below depends on the laid-out positions, not the input order.
  const orderedExternals = sortExternalsByCluster(visibleExternals);

  const { total: totalRisks, containersWithRisk } = aggregateRiskCounts(arch);

  for (const actor of visibleActors) {
    nodes.push({
      id: `actor:${actor.id}`,
      type: "actor",
      position: { x: 0, y: 0 },
      data: { actor, compact: true } as ActorNodeData,
      width: ACTOR_NODE_SIZE.width,
      height: ACTOR_NODE_SIZE.height,
      zIndex: CONTENT_Z_INDEX,
    });
  }

  // Centerpiece: v0.3's authored System entity if present, else the v0.2
  // abstract boundary. Both occupy the same id "system" so the radial
  // layout treats them identically; the SystemFrame placement code reads
  // node.type to decide drag/drill behaviour.
  if (arch.system) {
    nodes.push({
      id: "system",
      type: "system",
      position: { x: 0, y: 0 },
      data: {
        system: arch.system,
        lifecycleStage: arch.meta.lifecycle_stage,
        containerCount: arch.containers.length,
        aggregatedRiskCount: totalRisks,
        containersWithRiskCount: containersWithRisk,
      } as SystemNodeData,
      width: SYSTEM_NODE_SIZE.width,
      height: SYSTEM_NODE_SIZE.height,
      zIndex: CONTENT_Z_INDEX,
    });
  } else {
    nodes.push({
      id: "system",
      type: "systemBoundary",
      position: { x: 0, y: 0 },
      data: {
        label: arch.meta.name,
        description: arch.meta.description,
        containerCount: arch.containers.length,
        clickable: true,
        hero: true,
        aggregatedRiskCount: totalRisks,
        containersWithRiskCount: containersWithRisk,
      } as SystemBoundaryNodeData,
      width: SYSTEM_NODE_SIZE.width,
      height: SYSTEM_NODE_SIZE.height,
      zIndex: CONTENT_Z_INDEX,
    });
  }

  for (const ext of orderedExternals) {
    const drill = computeExternalDrillHint(ext, arch);
    nodes.push({
      id: `external:${ext.id}`,
      type: "externalSystem",
      position: { x: 0, y: 0 },
      data: {
        system: ext,
        activeOverlay: overlay,
        compact: true,
        drillState: drill.state,
        drillTitle: drill.title,
      } as ExternalSystemNodeData,
      width: EXTERNAL_NODE_SIZE_COMPACT.width,
      height: EXTERNAL_NODE_SIZE_COMPACT.height,
      zIndex: CONTENT_Z_INDEX,
    });
  }

  // Actor → system: one dashed edge per involved actor. The canonical edges
  // already encode actor→container relationships, but at L1 we collapse all
  // containers to the central system box, so we synthesize one aggregated
  // edge per actor. Label comes from the first matching canonical edge.
  for (const actor of visibleActors) {
    const sample =
      ctx.edges.find((e) => e.from === actor.id) ?? ctx.edges.find((e) => e.to === actor.id);
    const labelInfo = sample ? decorateEdge(ctx, sample) : {};
    edges.push({
      id: `e:actor:${actor.id}->system`,
      source: `actor:${actor.id}`,
      target: "system",
      style: { strokeDasharray: "4 4", stroke: "var(--muted-foreground)" },
      markerEnd: EDGE_MARKER,
      ...labelInfo,
    });
  }

  // System → external: one edge per involved external. Aggregate so multiple
  // containers connecting to the same external collapse into a single edge.
  // Skip externals that have NO referencing flow — drawing a line to them
  // would imply a connection that doesn't exist in the architecture. They
  // still render as nodes (see visibleExternals above); they just sit in the
  // ring unconnected, signalling "documented but not wired up."
  for (const ext of visibleExternals) {
    const sample =
      ctx.edges.find((e) => e.to === ext.id && ctx.containerIds.has(e.from)) ??
      ctx.edges.find((e) => e.from === ext.id && ctx.containerIds.has(e.to));
    if (!sample) continue;
    const edgeId = `e:system->external:${ext.id}`;
    if (!edges.some((e) => e.id === edgeId)) {
      const labelInfo = decorateEdge(ctx, sample);
      edges.push({
        id: edgeId,
        source: "system",
        target: `external:${ext.id}`,
        markerEnd: EDGE_MARKER,
        ...labelInfo,
      });
    }
  }

  const laidOut = radialLayout(nodes, edges, { systemId: "system", mode: layoutMode });

  // Emit a dashed clusterFrame around each cluster's laid-out externals.
  // Same chrome + z-stack as the L2 sub-boundaries (gap #6/2; z-stack
  // normalised to edges(0) < cluster(5) < content(10) in gap #6/9).
  const externalNodesById = new Map(
    laidOut.nodes.filter((n) => n.type === "externalSystem").map((n) => [n.id, n] as const),
  );
  const clusterFrames: Node[] = [];
  const grouped = groupExternalsByCluster(orderedExternals);
  for (const [clusterId, members] of grouped) {
    const first = members[0];
    if (!first?.cluster) continue;
    const contained: Node[] = [];
    for (const ext of members) {
      const node = externalNodesById.get(`external:${ext.id}`);
      if (node) contained.push(node);
    }
    if (contained.length === 0) continue;
    const frame = makeClusterFrame(
      `cluster:${clusterId}`,
      first.cluster.label.toUpperCase(),
      composeClusterSubtitle(arch.meta, clusterId),
      "external",
      "dashed", // L1 cluster is standalone (no parent frame above it)
      contained,
      {
        w: EXTERNAL_NODE_SIZE_COMPACT.width,
        h: EXTERNAL_NODE_SIZE_COMPACT.height,
      },
    );
    if (frame) clusterFrames.push(frame);
  }
  const finalNodes = [...clusterFrames, ...laidOut.nodes];
  return { nodes: finalNodes, edges: routeEdges(finalNodes, laidOut.edges, 1) };
}

function buildL2(arch: ArchitectureData, overlay: OverlayMode, ctx: BuildContext) {
  const containerNodes: Node[] = [];
  const edges: Edge[] = [];
  const externalRefs = new Set<string>();
  const involvedActors = new Set<string>();

  for (const c of arch.containers) {
    containerNodes.push({
      id: `container:${c.id}`,
      type: "container",
      position: { x: 0, y: 0 },
      data: {
        container: c,
        activeOverlay: overlay,
        connectsToExternals: ctx.containerExternals.get(c.id) ?? new Set<string>(),
      } as ContainerNodeData,
      width: CONTAINER_NODE_SIZE.width,
      height: CONTAINER_NODE_SIZE.height,
      zIndex: CONTENT_Z_INDEX,
    });
  }

  for (const e of ctx.edges) {
    const fromIsContainer = ctx.containerIds.has(e.from);
    const toIsContainer = ctx.containerIds.has(e.to);
    const fromIsExternal = ctx.externalIds.has(e.from);
    const toIsExternal = ctx.externalIds.has(e.to);
    const fromIsActor = ctx.actorIds.has(e.from);

    if (fromIsContainer && toIsContainer) {
      const labelInfo = decorateEdge(ctx, e);
      edges.push({
        id: `e:${e.from}->${e.to}`,
        source: `container:${e.from}`,
        target: `container:${e.to}`,
        markerEnd: EDGE_MARKER,
        ...labelInfo,
      });
    } else if (fromIsContainer && toIsExternal) {
      externalRefs.add(e.to);
      const labelInfo = decorateEdge(ctx, e);
      edges.push({
        id: `e:${e.from}->external:${e.to}`,
        source: `container:${e.from}`,
        target: `external:${e.to}`,
        markerEnd: EDGE_MARKER,
        ...labelInfo,
      });
    } else if (fromIsExternal && toIsContainer) {
      externalRefs.add(e.from);
      const labelInfo = decorateEdge(ctx, e);
      edges.push({
        id: `e:external:${e.from}->${e.to}`,
        source: `external:${e.from}`,
        target: `container:${e.to}`,
        markerEnd: EDGE_MARKER,
        ...labelInfo,
      });
    } else if (fromIsActor && toIsContainer) {
      involvedActors.add(e.from);
    }
  }

  const actorNodes: Node[] = [];
  for (const id of involvedActors) {
    const actor = arch.actors.find((a) => a.id === id);
    if (!actor) continue;
    actorNodes.push({
      id: `actor:${actor.id}`,
      type: "actor",
      position: { x: 0, y: 0 },
      // compact:true keeps the L2 actor card label-only (matches L1) so a
      // chatty actor description doesn't bloat the card and drop its bottom
      // into the external column's y-band below. Full text remains in the
      // side panel.
      data: { actor, compact: true } as ActorNodeData,
      width: ACTOR_NODE_SIZE.width,
      height: ACTOR_NODE_SIZE.height,
      zIndex: CONTENT_Z_INDEX,
    });
    for (const e of ctx.edges) {
      if (e.from === id && ctx.containerIds.has(e.to)) {
        const labelInfo = decorateEdge(ctx, e);
        edges.push({
          id: `e:actor:${id}->${e.to}`,
          source: `actor:${id}`,
          target: `container:${e.to}`,
          style: { strokeDasharray: "4 4", stroke: "var(--muted-foreground)" },
          markerEnd: EDGE_MARKER,
          ...labelInfo,
        });
      }
    }
  }

  const externalNodes: Node[] = [];
  for (const extId of externalRefs) {
    const ext = arch.externalSystems.find((s) => s.id === extId);
    if (!ext) continue;
    const drill = computeExternalDrillHint(ext, arch);
    externalNodes.push({
      id: `external:${ext.id}`,
      type: "externalSystem",
      position: { x: 0, y: 0 },
      data: {
        system: ext,
        activeOverlay: overlay,
        drillState: drill.state,
        drillTitle: drill.title,
      } as ExternalSystemNodeData,
      width: EXTERNAL_NODE_SIZE_EXPANDED.width,
      height: EXTERNAL_NODE_SIZE_EXPANDED.height,
      zIndex: CONTENT_Z_INDEX,
    });
  }

  // Layout pass 1: containers into kind-rank rows.
  const containerInputs = arch.containers.map((c) => ({
    id: `container:${c.id}`,
    kind: c.kind ?? c.type,
  }));
  const { placed, bbox } = layoutContainersByRank(containerInputs);
  const placedContainers = applyContainerPositions(containerNodes, placed);
  const rankById = new Map<string, 0 | 1 | 2 | 3>(placed.map((p) => [p.id, p.rank]));

  // Layout pass 2: externals around the container frame perimeter.
  const externalInputs = externalNodes.map((n) => {
    const extId = n.id.replace(/^external:/, "");
    // Find every container this external touches by walking the canonical
    // edges. Used to pick which side of the perimeter it lands on.
    const connectedContainerIds: string[] = [];
    for (const e of ctx.edges) {
      if (e.from === extId && ctx.containerIds.has(e.to)) {
        connectedContainerIds.push(`container:${e.to}`);
      } else if (e.to === extId && ctx.containerIds.has(e.from)) {
        connectedContainerIds.push(`container:${e.from}`);
      }
    }
    const extData = arch.externalSystems.find((s) => s.id === extId);
    return { id: n.id, kind: extData?.kind ?? extData?.type, connectedContainerIds };
  });
  // Expand the bbox for the external-column placement to account for the
  // actor row above. The actor row is centered on the container bbox but
  // can be wider than it when there are more actors than containers per
  // row — without this, the rightmost actor and the external column share
  // x-pixels and visually collide (e.g. Wolf DMS: 4 actors @ 160 px wide
  // sit on a 280 px-wide container column, pushing the actor row out to
  // x = ±368 while the external column starts at x = 260).
  const actorRowTotalW =
    actorNodes.length === 0
      ? 0
      : actorNodes.length * ACTOR_NODE_SIZE.width + (actorNodes.length - 1) * 32;
  const containerCenterX = (bbox.minX + bbox.maxX) / 2;
  const widenedBbox = {
    minX: Math.min(bbox.minX, containerCenterX - actorRowTotalW / 2),
    minY: bbox.minY,
    maxX: Math.max(bbox.maxX, containerCenterX + actorRowTotalW / 2),
    maxY: bbox.maxY,
  };
  const externalPlacements = layoutExternalsAroundPerimeter(externalInputs, rankById, widenedBbox);
  const placedExternals = applyExternalPositions(externalNodes, externalPlacements);

  // Actors: above the container frame, centered. They're not in the
  // perimeter ring — they're outside it on the user's side (top).
  if (actorNodes.length > 0) {
    const totalW = actorNodes.length * ACTOR_NODE_SIZE.width + (actorNodes.length - 1) * 32;
    const centerX = (bbox.minX + bbox.maxX) / 2;
    const startX = centerX - totalW / 2;
    const actorY = bbox.minY - 200; // above the top-of-frame externals
    for (let i = 0; i < actorNodes.length; i++) {
      const node = actorNodes[i];
      if (!node) continue;
      node.position = { x: startX + i * (ACTOR_NODE_SIZE.width + 32), y: actorY };
    }
  }

  // Sub-boundaries inside the BUILD frame: BACKGROUND WORKERS wraps the
  // rank-2 (worker) row, EXTERNAL SERVICES wraps the perimeter externals
  // (now consolidated to a single right-side column by kind-rank-layout).
  // Z-stack (gap #6/9): systemFrame(-2) < edges(0) < these cluster
  // frames(5) < containers/externals(10), so edges crossing into the
  // cluster pass under its dashed border, and the contained nodes sit
  // on top of the cluster border where they overlap.
  const workerContainers = placedContainers.filter((n) => rankById.get(n.id) === 2);
  const workerCluster = makeClusterFrame(
    "cluster:workers",
    "BACKGROUND WORKERS",
    composeClusterSubtitle(arch.meta, "workers"),
    "worker",
    "solid", // L2 cluster nested inside system frame
    workerContainers,
    {
      w: CONTAINER_NODE_SIZE.width,
      h: CONTAINER_NODE_SIZE.height,
    },
  );
  const externalCluster = makeClusterFrame(
    "cluster:externals",
    "EXTERNAL SERVICES",
    composeClusterSubtitle(arch.meta, "externals"),
    "external",
    "solid", // L2 cluster nested inside system frame
    placedExternals,
    { w: EXTERNAL_NODE_SIZE_EXPANDED.width, h: EXTERNAL_NODE_SIZE_EXPANDED.height },
  );

  const clusterFrames: Node[] = [];
  if (workerCluster) clusterFrames.push(workerCluster);
  if (externalCluster) clusterFrames.push(externalCluster);

  const allNodes = [...clusterFrames, ...placedContainers, ...placedExternals, ...actorNodes];
  const sys = composeSystemFrameLabel(arch.meta);
  const framed = addBoundaryFrame(allNodes, sys.label, sys.subtitle, "container", "system-frame");
  return { nodes: framed, edges: routeEdges(framed, edges, 2) };
}

function buildL3(
  arch: ArchitectureData,
  containerId: string,
  overlay: OverlayMode,
  ctx: BuildContext,
) {
  const container = arch.containers.find((c) => c.id === containerId);
  if (!container) return { nodes: [], edges: [] };
  // Empty-state: container has no components documented. Previously this
  // returned an empty graph and rendered a blank canvas with no message.
  // Now we surface a single systemFrame with an explanatory subtitle so
  // users know it's intentional emptiness, not a render failure.
  if (container.components.length === 0) {
    return emptyL3Frame(
      container.label.toUpperCase(),
      "NO COMPONENTS DOCUMENTED FOR THIS CONTAINER",
    );
  }
  const nodes: Node[] = [];
  const edges: Edge[] = [];
  const componentIds = new Set(container.components.map((c) => c.id));

  for (const comp of container.components) {
    nodes.push({
      id: `component:${comp.id}`,
      type: "component",
      position: { x: 0, y: 0 },
      data: {
        component: comp,
        parentKind: container.kind,
        activeOverlay: overlay,
        connectsToExternals: ctx.componentExternals.get(comp.id) ?? new Set<string>(),
      } as ComponentNodeData,
      width: COMPONENT_NODE_SIZE.width,
      height: COMPONENT_NODE_SIZE.height,
      zIndex: CONTENT_Z_INDEX,
    });
    // Per-component edges are a v0.2-only concept; v0.3 keeps L3 as nodes
    // only (per OI-2). Walking component.connects_to[] here is safe for both
    // because v0.3 leaves it undefined.
    for (const target of comp.connects_to ?? []) {
      if (componentIds.has(target)) {
        const edge: CanonicalEdge = {
          from: comp.id,
          to: target,
          label: findStepLabel(arch, comp.id, target, ctx.componentToContainer),
        };
        const labelInfo = decorateEdge(ctx, edge);
        edges.push({
          id: `e:${comp.id}->${target}`,
          source: `component:${comp.id}`,
          target: `component:${target}`,
          markerEnd: EDGE_MARKER,
          ...labelInfo,
        });
      } else {
        const resolved = resolveId(arch, target);
        if (resolved.kind === "external-system") {
          const refId = `external:${target}`;
          if (!nodes.some((n) => n.id === refId)) {
            const ext = arch.externalSystems.find((s) => s.id === target);
            if (ext) {
              const drill = computeExternalDrillHint(ext, arch);
              nodes.push({
                id: refId,
                type: "externalSystem",
                position: { x: 0, y: 0 },
                data: {
                  system: ext,
                  activeOverlay: overlay,
                  drillState: drill.state,
                  drillTitle: drill.title,
                } as ExternalSystemNodeData,
                width: EXTERNAL_NODE_SIZE_EXPANDED.width,
                height: EXTERNAL_NODE_SIZE_EXPANDED.height,
                zIndex: CONTENT_Z_INDEX,
              });
            }
          }
          const edge: CanonicalEdge = {
            from: comp.id,
            to: target,
            label: findStepLabel(arch, comp.id, target, ctx.componentToContainer),
          };
          const labelInfo = decorateEdge(ctx, edge);
          edges.push({
            id: `e:${comp.id}->external:${target}`,
            source: `component:${comp.id}`,
            target: refId,
            markerEnd: EDGE_MARKER,
            ...labelInfo,
          });
        }
      }
    }
  }

  const laidOut = layoutGraph(nodes, edges, {
    rankdir: "TB",
    nodesep: 24,
    ranksep: 80,
    ranker: "tight-tree",
  });
  const framed = addBoundaryFrame(
    laidOut.nodes,
    container.label.toUpperCase(),
    undefined, // L3 container drill-in has no runtime_context surface today
    "component",
    "container-frame",
  );
  return { nodes: framed, edges: routeEdges(framed, laidOut.edges, 3) };
}

// === Data Model (L3 of a data-storage container) ===========================

const ENTITY_NODE_WIDTH = 264;
const ENTITY_HEADER_HEIGHT = 34;
const ENTITY_ROW_HEIGHT = 22;
const ENTITY_SIMPLE_HEIGHT = 64;

function entityNodeHeight(entity: Entity, density: "simple" | "detailed"): number {
  const fieldCount = entity.fields?.length ?? 0;
  if (density === "simple" || fieldCount === 0) return ENTITY_SIMPLE_HEIGHT;
  return ENTITY_HEADER_HEIGHT + fieldCount * ENTITY_ROW_HEIGHT + 8;
}

function humanizeCardinality(c: EntityRelationship["cardinality"]): string | undefined {
  switch (c) {
    case "one-to-one":
      return "1:1";
    case "one-to-many":
      return "1:N";
    case "many-to-many":
      return "N:N";
    default:
      return undefined;
  }
}

const DM_EDGE_LABEL_STYLE: CSSProperties = { fontSize: 10, fill: "var(--muted-foreground)" };
const DM_EDGE_LABEL_BG_STYLE: CSSProperties = { fill: "var(--card)", fillOpacity: 0.9 };

// Crow's-foot endpoints, expressed as the SVG marker urls defined by
// <ErdEdgeMarkers/>. markerStart sits at the edge source, markerEnd at the
// target. "one" is a perpendicular bar; "many" is the crow's-foot fork.
const ERD_MARKER_ONE = "url(#erd-one)";
const ERD_MARKER_MANY = "url(#erd-many)";

// Map a (source, target) relationship to its crow's-foot markers. `cardinality`
// comes from an authored EntityRelationship when present; otherwise the edge is
// FK-derived (the source holds the FK = the "many" child, the target is the
// referenced "one" parent), which is the physical truth for the detailed view
// and the sensible default for conceptual FK pairs.
export function erdMarkers(cardinality?: EntityRelationship["cardinality"]): {
  markerStart: string;
  markerEnd: string;
} {
  switch (cardinality) {
    case "one-to-one":
      return { markerStart: ERD_MARKER_ONE, markerEnd: ERD_MARKER_ONE };
    case "one-to-many":
      return { markerStart: ERD_MARKER_ONE, markerEnd: ERD_MARKER_MANY };
    case "many-to-many":
      return { markerStart: ERD_MARKER_MANY, markerEnd: ERD_MARKER_MANY };
    default:
      // FK-derived: many (source/child) → one (target/parent).
      return { markerStart: ERD_MARKER_MANY, markerEnd: ERD_MARKER_ONE };
  }
}

// Detailed/ERD edges: one per foreign-key field, entity → referenced entity.
// Self-references and FKs pointing outside the model are skipped (dagre
// doesn't lay out self-loops cleanly, and a dangling target has no node).
// Crow's-foot markers + smoothstep (orthogonal) routing make them read like a
// physical ERD.
function buildErdEdges(model: DataModel, entityIds: ReadonlySet<string>): Edge[] {
  const edges: Edge[] = [];
  for (const entity of model.entities) {
    for (const field of entity.fields ?? []) {
      const target = field.fk?.entity;
      if (!target || target === entity.id || !entityIds.has(target)) continue;
      edges.push({
        id: `dm:${entity.id}.${field.name}->${target}`,
        source: `entity:${entity.id}`,
        target: `entity:${target}`,
        type: "smoothstep",
        ...erdMarkers(),
      });
    }
  }
  return edges;
}

// Simple/conceptual edges: the deduped entity-pair set of all FKs, plus any
// authored relationship pairs (which can express links no single FK can, e.g.
// many-to-many). Labels come from the authored relationship — its plain-text
// `label`, else a humanized cardinality.
function buildConceptualEdges(model: DataModel, entityIds: ReadonlySet<string>): Edge[] {
  const pairKey = (from: string, to: string) => `${from}|${to}`;
  const pairs = new Map<string, { from: string; to: string }>();

  for (const entity of model.entities) {
    for (const field of entity.fields ?? []) {
      const target = field.fk?.entity;
      if (!target || target === entity.id || !entityIds.has(target)) continue;
      pairs.set(pairKey(entity.id, target), { from: entity.id, to: target });
    }
  }
  for (const rel of model.relationships ?? []) {
    if (rel.from === rel.to || !entityIds.has(rel.from) || !entityIds.has(rel.to)) continue;
    pairs.set(pairKey(rel.from, rel.to), { from: rel.from, to: rel.to });
  }

  const relationships = model.relationships ?? [];
  const relFor = (from: string, to: string): EntityRelationship | undefined =>
    relationships.find((r) => r.from === from && r.to === to);

  const edges: Edge[] = [];
  for (const { from, to } of pairs.values()) {
    const rel = relFor(from, to);
    const label = rel ? (rel.label ?? humanizeCardinality(rel.cardinality)) : undefined;
    edges.push({
      id: `dm:${from}~${to}`,
      source: `entity:${from}`,
      target: `entity:${to}`,
      type: "smoothstep",
      // Authored cardinality drives the crow's-foot; FK-only pairs fall back to
      // the many→one default inside erdMarkers().
      ...erdMarkers(rel?.cardinality),
      ...(label
        ? { label, labelStyle: DM_EDGE_LABEL_STYLE, labelBgStyle: DM_EDGE_LABEL_BG_STYLE }
        : {}),
    });
  }
  return edges;
}

// L3 "Data Model" view for a data-storage container. Entities as boxes; edges
// derived from FKs (+ authored relationship labels in Simple mode). Mirrors
// buildL3's frame + dagre + routeEdges shape so it shares the canvas chrome.
function buildDataModel(
  arch: ArchitectureData,
  containerId: string,
  density: "simple" | "detailed",
): { nodes: Node[]; edges: Edge[] } {
  const container = arch.containers.find((c) => c.id === containerId);
  const model = container?.dataModel;
  if (!container || !model || model.entities.length === 0) {
    return emptyL3Frame(
      `${(container?.label ?? "Data model").toUpperCase()} · DATA MODEL`,
      "NO DATA MODEL DOCUMENTED FOR THIS CONTAINER",
    );
  }

  const entityIds = new Set(model.entities.map((e) => e.id));
  const nodes: Node[] = model.entities.map((entity) => ({
    id: `entity:${entity.id}`,
    type: "entity",
    position: { x: 0, y: 0 },
    data: { entity, density } as EntityNodeData,
    width: ENTITY_NODE_WIDTH,
    height: entityNodeHeight(entity, density),
    zIndex: CONTENT_Z_INDEX,
  }));

  const edges =
    density === "detailed"
      ? buildErdEdges(model, entityIds)
      : buildConceptualEdges(model, entityIds);

  // Wider node/rank separation than the component view: crow's-foot markers +
  // orthogonal routing need clear channels between entity tables to stay legible.
  const laidOut = layoutGraph(nodes, edges, {
    rankdir: "TB",
    nodesep: 64,
    ranksep: 130,
    ranker: "tight-tree",
  });
  const framed = addBoundaryFrame(
    laidOut.nodes,
    `${container.label.toUpperCase()} · DATA MODEL`,
    density === "simple" ? "BUSINESS DATA MODEL" : "PHYSICAL SCHEMA",
    "entity",
    "data-model-frame",
  );
  return { nodes: framed, edges: routeEdges(framed, laidOut.edges, 3) };
}

// Synthesize a labelled frame for an empty L3 view. The frame has fixed
// generous dimensions so the message reads clearly without a content
// bbox to anchor it. Used by both empty-container L3 and as a defensive
// fallback for L3-of-external when somehow drilled in with no surface.
function emptyL3Frame(label: string, subtitle: string): { nodes: Node[]; edges: Edge[] } {
  const W = 560;
  const H = 220;
  return {
    nodes: [
      {
        id: "empty-l3-frame",
        type: "systemFrame",
        position: { x: 0, y: 0 },
        data: { label, subtitle },
        width: W,
        height: H,
        style: { width: W, height: H },
        draggable: false,
        selectable: false,
        zIndex: -2,
      },
    ],
    edges: [],
  };
}

const ENDPOINT_NODE_SIZE = { width: 220, height: 80 };
const PERMISSION_BAND_HEIGHT = 64;
const UNGATED_HEADER_HEIGHT = 56;
const L3X_COL_GAP = 24;
const L3X_ROW_GAP = 16;
const L3X_HEADER_TO_ENDPOINTS_GAP = 16;
const L3X_GROUP_GAP = 48;

// L3-of-external view (v0.4). Stacks each permission as a full-width header
// band followed by the endpoints it gates, laid out in a `columns`-wide grid.
// Severity-first ordering and the ungated bucket come from
// groupEndpointsByPermission. Connector edges are intentionally dropped — the
// spatial grouping conveys the permission->endpoint relationship. `columns` is
// supplied by the caller from the measured canvas width (default 4).
function buildL3External(
  arch: ArchitectureData,
  externalId: string,
  _overlay: OverlayMode,
  columns: number,
): { nodes: Node[]; edges: Edge[] } {
  const ext = arch.externalSystems.find((e) => e.id === externalId);
  if (!ext) return { nodes: [], edges: [] };

  const permissions = ext.permissions ?? [];
  const endpoints = ext.endpoints ?? [];
  // Defensive: the click handler in GraphCanvas already blocks drill-in for
  // externals with no API surface. If state was hand-edited to drill into one
  // anyway, fall back to the same empty-state frame as L3-of-container.
  if (permissions.length === 0 && endpoints.length === 0) {
    return emptyL3Frame(
      `${ext.label.toUpperCase()} · API SURFACE`,
      "NO PERMISSIONS OR ENDPOINTS DOCUMENTED FOR THIS EXTERNAL",
    );
  }

  const cols = Math.max(1, columns);
  const bandWidth = cols * ENDPOINT_NODE_SIZE.width + (cols - 1) * L3X_COL_GAP;
  const { groups, ungated } = groupEndpointsByPermission(permissions, endpoints);
  const nodes: Node[] = [];
  let y = 0;

  // Place a wrapped grid of endpoint cards starting at `startY`; returns the
  // y coordinate just past the grid's bottom edge.
  const placeEndpointGrid = (eps: typeof endpoints, startY: number): number => {
    if (eps.length === 0) return startY;
    const rows = Math.ceil(eps.length / cols);
    eps.forEach((endpoint, i) => {
      const col = i % cols;
      const row = Math.floor(i / cols);
      nodes.push({
        id: `endpoint:${endpoint.id}`,
        type: "endpoint",
        position: {
          x: col * (ENDPOINT_NODE_SIZE.width + L3X_COL_GAP),
          y: startY + row * (ENDPOINT_NODE_SIZE.height + L3X_ROW_GAP),
        },
        data: { endpoint } as EndpointNodeData,
        width: ENDPOINT_NODE_SIZE.width,
        height: ENDPOINT_NODE_SIZE.height,
        zIndex: CONTENT_Z_INDEX,
      });
    });
    return startY + rows * ENDPOINT_NODE_SIZE.height + (rows - 1) * L3X_ROW_GAP;
  };

  for (const group of groups) {
    nodes.push({
      id: `permission:${group.permission.scope}`,
      type: "permission",
      position: { x: 0, y },
      data: {
        permission: group.permission,
        band: true,
        endpointCount: group.endpoints.length,
      } as PermissionNodeData,
      width: bandWidth,
      height: PERMISSION_BAND_HEIGHT,
      style: { width: bandWidth, height: PERMISSION_BAND_HEIGHT },
      zIndex: CONTENT_Z_INDEX,
    });
    y += PERMISSION_BAND_HEIGHT + L3X_HEADER_TO_ENDPOINTS_GAP;
    y = placeEndpointGrid(group.endpoints, y);
    y += L3X_GROUP_GAP;
  }

  if (ungated.length > 0) {
    nodes.push({
      id: "ungated-header",
      type: "systemFrame",
      position: { x: 0, y },
      data: { label: "UNGATED ENDPOINTS", subtitle: "No documented permission gate" },
      width: bandWidth,
      height: UNGATED_HEADER_HEIGHT,
      style: { width: bandWidth, height: UNGATED_HEADER_HEIGHT },
      draggable: false,
      selectable: false,
      zIndex: CONTENT_Z_INDEX,
    });
    y += UNGATED_HEADER_HEIGHT + L3X_HEADER_TO_ENDPOINTS_GAP;
    y = placeEndpointGrid(ungated, y);
  }

  // Outer boundary frame around the whole stack.
  const bbox = computeBBox(nodes, {
    w: ENDPOINT_NODE_SIZE.width,
    h: ENDPOINT_NODE_SIZE.height,
  });
  if (bbox) {
    const PAD = 60;
    const TOP_PAD = 70;
    nodes.unshift({
      id: "external-frame",
      type: "systemFrame",
      position: { x: bbox.minX - PAD, y: bbox.minY - TOP_PAD },
      data: { label: `${ext.label.toUpperCase()} · API SURFACE`, subtitle: undefined },
      width: bbox.maxX - bbox.minX + PAD * 2,
      height: bbox.maxY - bbox.minY + TOP_PAD + PAD,
      style: {
        width: bbox.maxX - bbox.minX + PAD * 2,
        height: bbox.maxY - bbox.minY + TOP_PAD + PAD,
      },
      draggable: false,
      selectable: false,
      zIndex: -2,
    });
  }

  return { nodes, edges: [] };
}

export interface UseGraphElementsOptions {
  // Column count for the L3-of-external endpoint grid. Bucketed (2/3/4) from
  // the measured canvas width by GraphCanvas. Defaults to 4 for static/shared
  // views that have no live resizing canvas.
  l3ExternalColumns?: number;
}

export function useGraphElements(
  state: ArchVizState,
  options?: UseGraphElementsOptions,
): { nodes: Node[]; edges: Edge[] } {
  const l3ExternalColumns = options?.l3ExternalColumns ?? 4;
  // Memoize on the topology-driving fields only. Selection (selectedNodeId)
  // does not change the graph shape — GraphCanvas decorates separately for
  // focus/dim. Keying on the whole state object would recompute every click.
  const {
    architecture,
    drillLevel,
    selectedContainerId,
    selectedExternalId,
    dataView,
    dataModelDensity,
    overlay,
    showLabels,
    layoutMode,
  } = state;
  return useMemo(() => {
    if (!architecture) return { nodes: [], edges: [] };

    const componentToContainer = buildComponentToContainerMap(architecture);
    const externalIds = new Set(architecture.externalSystems.map((e) => e.id));
    const containerIds = new Set(architecture.containers.map((c) => c.id));
    const actorIds = new Set(architecture.actors.map((a) => a.id));
    const edges = canonicalEdges(architecture, componentToContainer);
    const containerExternals = buildContainerExternalAdjacency(edges, containerIds, externalIds);
    const componentExternals = buildComponentExternalAdjacency(architecture, externalIds);

    const baseCtx = {
      componentToContainer,
      externalIds,
      containerIds,
      actorIds,
      edges,
      containerExternals,
      componentExternals,
    };

    if (drillLevel === 1) {
      const ctx: BuildContext = { ...baseCtx, labelMode: "hover-only" };
      return buildL1(architecture, overlay, ctx, layoutMode);
    }
    const ctx: BuildContext = {
      ...baseCtx,
      // Default labelMode at L2/L3 is "hover-only" — the canvas reads clean
      // at rest, edges expose their labels on direct hover or when a connected
      // node is selected (handled by GraphCanvas focus-reveal). The legacy
      // "always" mode is kept as an opt-in for users who want the dense view.
      labelMode: showLabels ? "always" : "hover-only",
    };
    if (drillLevel === 2) return buildL2(architecture, overlay, ctx);
    if (drillLevel === 3 && selectedContainerId && dataView === "dataModel") {
      return buildDataModel(architecture, selectedContainerId, dataModelDensity);
    }
    if (drillLevel === 3 && selectedContainerId) {
      return buildL3(architecture, selectedContainerId, overlay, ctx);
    }
    if (drillLevel === 3 && selectedExternalId) {
      return buildL3External(architecture, selectedExternalId, overlay, l3ExternalColumns);
    }
    return buildL2(architecture, overlay, ctx);
  }, [
    architecture,
    drillLevel,
    selectedContainerId,
    selectedExternalId,
    dataView,
    dataModelDensity,
    overlay,
    showLabels,
    layoutMode,
    l3ExternalColumns,
  ]);
}
